import { Button } from "@/shared/components";

import { formatInvoiceMoney, INVOICE_PREVIEW_COLUMNS } from "../../constants/invoiceLineColumns";
import { INVOICE_LOCALISATION } from "../../constants/localisation";
import type { InvoicePreviewData } from "../../types";
import InvoiceLineTable from "../InvoiceLineTable";

type InvoicePreviewStepProps = {
  preview: InvoicePreviewData;
  isSubmitting?: boolean;
  onBack: () => void;
  onSaveDraft: () => void;
};

export default function InvoicePreviewStep({
  preview,
  isSubmitting = false,
  onBack,
  onSaveDraft,
}: InvoicePreviewStepProps) {
  const { taxSplit } = preview;
  const hasLines = preview.lineCount > 0;

  const summaryTiles = [
    {
      key: "lines",
      label: "Lines",
      value: preview.lineCount.toLocaleString(INVOICE_LOCALISATION.numberLocale),
    },
    {
      key: "commission",
      label: "Commission",
      value: formatInvoiceMoney(preview.commissionTotal),
    },
    {
      key: "tax",
      label: taxSplit.taxLabel,
      value: formatInvoiceMoney(preview.taxTotal),
      hint: taxSplit.label,
    },
    {
      key: "total",
      label: "Invoice total",
      value: formatInvoiceMoney(preview.grandTotal),
      highlight: true,
    },
  ];

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-x-8 gap-y-5 rounded-lg border border-theme-border bg-theme-surface-soft p-4 sm:grid-cols-2">
        {summaryTiles.map((tile) => (
          <div key={tile.key}>
            <p className="text-xs font-semibold uppercase tracking-wide text-theme-text-soft">
              {tile.label}
            </p>
            <p
              className={`mt-1 text-xl font-bold ${
                tile.highlight ? "text-theme-primary" : "text-theme-text"
              }`}
            >
              {tile.value}
            </p>
            {tile.hint ? <p className="mt-0.5 text-xs text-theme-text-soft">{tile.hint}</p> : null}
          </div>
        ))}
      </div>

      <InvoiceLineTable
        columns={INVOICE_PREVIEW_COLUMNS}
        rows={preview.lines}
        emptyText="No commission lines found for the selected insurer and period"
      />

      <div className="flex justify-end gap-3 border-t border-theme-border pt-4">
        <Button type="button" variant="secondary" disabled={isSubmitting} onClick={onBack}>
          Back
        </Button>
        <Button
          type="button"
          disabled={isSubmitting || !hasLines}
          isLoading={isSubmitting}
          onClick={onSaveDraft}
        >
          Save as Draft
        </Button>
      </div>
    </div>
  );
}
